import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import authService from "../../services/authService";
import { RootState } from "../../store/store";

import logo from "../../assets/logo.png";

const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [timer, setTimer] = useState(30);

  const siteName = process.env.REACT_APP_SITE_NAME || "XRP Trading Platform";

  useEffect(() => {
    const stateEmail = location.state?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    } else if (user?.email) {
      // Try to get email from auth state
      setEmail(user.email);
    } else {
      // Redirect to login if no email found
      navigate("/login");
    }
  }, [location, navigate, user]); // Add user to dependency array

  useEffect(() => {
    if (success) {
      console.log("Success state is true, should redirect in 1.5 seconds...");
      const timer = setTimeout(() => {
        console.log("Timeout fired, navigating to /login");
        navigate("/login");
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  useEffect(() => {
    if (timer > 0 && !success) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer, success]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value !== "" && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter a 6-digit OTP");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await authService.verifyEmail(email, otpString);
      console.log("Verification response:", response);

      // Set success state
      setSuccess(true);

      // Clear the OTP inputs
      setOtp(["", "", "", "", "", ""]);

      // Redirect immediately or after short delay
      setTimeout(() => {
        console.log("Redirecting to login after verification...");
        navigate("/login");
      }, 1500); // Reduced from 2000 to 1500ms
    } catch (err: any) {
      console.error("Verification error:", err);
      setError(err.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResendLoading(true);
      setError(null);
      await authService.resendVerification(email);
      setResendSuccess(true);
      setTimer(60); // Reset timer to 1 minutes
      setOtp(["", "", "", "", "", ""]);

      // Focus first input
      const firstInput = document.getElementById("otp-0");
      if (firstInput) firstInput.focus();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className={globalStyle.container}>
      {/* <Header /> */}

      <main className="page-content2">
        <div className="page-content2-bg"></div>
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            <div className="max-w-md mx-auto py-8 md:py-12">
              <Link to="/" className="text-center mb-8">
                <img
                  src={logo}
                  alt={siteName}
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className={globalStyle.h3}>Verify Your Email</h2>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  Enter the 6-digit code sent to
                  <br />
                  <strong className="text-white">{email}</strong>
                </p>
              </Link>

              <Card padding="lg" className="mt-6">
                {error && (
                  <Alert
                    type="error"
                    message={error}
                    onClose={() => setError(null)}
                    className="mb-6"
                  />
                )}

                {success ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-green-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className={globalStyle.h5}>Email Verified!</h3>
                    <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                      Your email has been successfully verified.
                    </p>
                    <p className={`${globalStyle.caption} mt-4 text-gray-500`}>
                      Redirecting to login...
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-8">
                      <div className="flex justify-center space-x-2 mb-6">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            value={digit}
                            onChange={(e) =>
                              handleOtpChange(
                                index,
                                e.target.value.replace(/\D/g, ""),
                              )
                            }
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="w-12 h-12 text-center text-2xl font-semibold border-2 border-gray-700 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all duration-300 bg-gray-900 text-white"
                          />
                        ))}
                      </div>

                      <div className="flex items-center justify-center space-x-2">
                        <div
                          className={`text-sm ${
                            timer < 60 ? "text-red-500" : "text-gray-400"
                          }`}
                        >
                          ⏱️ {formatTime(timer)}
                        </div>
                        {timer === 0 && (
                          <button
                            onClick={handleResend}
                            className="text-sm text-primary hover:text-primary-300 font-medium"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </div>

                    {resendSuccess && (
                      <Alert
                        type="success"
                        message="New OTP sent to your email"
                        className="mb-6"
                      />
                    )}

                    <div className="space-y-4">
                      <Button
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={otp.join("").length !== 6}
                        fullWidth
                        size="lg"
                      >
                        Verify Email
                      </Button>

                      <Button
                        onClick={handleResend}
                        variant="secondary"
                        loading={resendLoading}
                        disabled={timer > 0}
                        fullWidth
                      >
                        {timer > 0
                          ? `Resend OTP in ${formatTime(timer)}`
                          : "Resend OTP"}
                      </Button>

                      <Link to="/login">
                        <Button variant="tertiary" fullWidth>
                          Back to Login
                        </Button>
                      </Link>
                    </div>
                  </>
                )}

                <div className="mt-8 pt-6 border-t border-gray-800">
                  <p className="text-center text-sm text-gray-400">
                    Didn't receive the email? Check your spam folder or{" "}
                    <button
                      onClick={handleResend}
                      className="text-primary font-medium hover:text-primary-300"
                      disabled={resendLoading}
                    >
                      resend
                    </button>
                  </p>
                </div>
              </Card>

              <div className="mt-8 text-center">
                <p className={globalStyle.caption + " text-gray-500"}>
                  The OTP is valid for 5 minutes. Keep it secure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmail;
